/**
 * Tenant-aware fetch wrapper for the React SPA.
 *
 * Auth strategy:
 *  1. If a real access token is in the session, attach `Authorization: Bearer`.
 *  2. Otherwise, in DEV-only, if `VITE_DEV_USER` is configured (a JSON object
 *     with id/storeId/role/email), attach the legacy `x-tcg-dev-user` header
 *     so the API's dev-mode auth shortcut accepts the request without a
 *     real login. The shortcut is rejected by the API in production.
 *  3. On a 401, attempt one silent `/auth/refresh` (which uses the HttpOnly
 *     refresh cookie) and replay the request once.
 *
 * The session is the single source of truth for `storeId`, `locationId`,
 * `registerId` — none of those are pulled from `import.meta.env` anymore.
 */
import {
  clearSession,
  getSession,
  oneHourFromNow,
  setAccessToken,
  setUser,
  type SessionUser,
} from './session';
import {
  createFirebaseOwner,
  firebaseEnabled,
  getFirebaseIdToken,
  requestFirebasePasswordReset,
  signInWithFirebaseEmail,
  signInWithFirebaseGoogle,
  signOutFirebase,
  waitForFirebaseUser,
} from './firebase';

const BASE = import.meta.env.VITE_API_URL ?? '';

/**
 * True when running inside a Capacitor native shell. In that environment
 * httpOnly cookies are not reliably sent with fetch, so we fall back to
 * passing the refresh token in the request body.
 */
function isNativePlatform(): boolean {
  return typeof window !== 'undefined' && 'Capacitor' in window;
}

function nativePlatformHeaders(): Record<string, string> {
  return isNativePlatform() ? { 'x-client-platform': 'capacitor' } : {};
}

function normalizedAccessToken(raw: string | null | undefined): string | null {
  const token = raw?.trim();
  return token ? token : null;
}

async function currentAccessToken(forceRefresh = false): Promise<string | null> {
  const firebaseToken = await getFirebaseIdToken(forceRefresh);
  return firebaseToken ?? normalizedAccessToken(getSession().accessToken);
}

export interface FormUploadProgress {
  loaded: number;
  total: number | null;
  percent: number | null;
}

export interface PostFormOptions {
  onUploadProgress?: (progress: FormUploadProgress) => void;
}

class FormUploadNetworkError extends Error {
  uploadedBytes: number;

  constructor(message: string, uploadedBytes: number) {
    super(message);
    this.name = 'FormUploadNetworkError';
    this.uploadedBytes = uploadedBytes;
  }
}

async function sendFormWithXhr(args: {
  path: string;
  form: FormData;
  headers: Record<string, string>;
  onUploadProgress?: (progress: FormUploadProgress) => void;
}): Promise<{ status: number; bodyText: string }> {
  const { path, form, headers, onUploadProgress } = args;

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let uploadedBytes = 0;
    xhr.open('POST', `${BASE}/api${path}`);
    xhr.withCredentials = true;

    Object.entries(headers).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value);
    });

    xhr.upload.onprogress = (event) => {
      uploadedBytes = event.loaded;
      if (!onUploadProgress) return;
      const total = event.lengthComputable ? event.total : null;
      const percent = total && total > 0 ? Math.min(100, Math.round((event.loaded / total) * 100)) : null;
      onUploadProgress({ loaded: event.loaded, total, percent });
    };

    xhr.onload = () => {
      resolve({
        status: xhr.status,
        bodyText: xhr.responseText ?? '',
      });
    };

    xhr.onerror = () =>
      reject(
        new FormUploadNetworkError(
          'Network error while uploading form data.',
          uploadedBytes,
        ),
      );
    xhr.onabort = () => reject(new Error('Form upload was aborted.'));

    xhr.send(form);
  });
}

function parseJsonBody<T>(status: number, bodyText: string): T {
  if (status === 204) return undefined as T;
  if (!bodyText.trim()) return undefined as T;
  return JSON.parse(bodyText) as T;
}

function devHeader(): string | null {
  if (!import.meta.env.DEV) return null;
  const session = getSession();
  if (!session.user || session.accessToken) return null;
  // Dev fallback: synthesise the legacy header from the (dev-bootstrapped) session.
  return JSON.stringify({
    id: session.user.id,
    storeId: session.user.storeId,
    role: session.user.role,
    email: session.user.email,
    displayName: session.user.displayName,
  });
}

interface RefreshResponse {
  accessToken: string;
  expiresIn: number;
  user: SessionUser;
  refreshToken?: string;
}

let _refreshInFlight: Promise<string | null> | null = null;

/**
 * Calls `/api/auth/refresh` to mint a new access token from the HttpOnly
 * refresh cookie (browser) or the stored refresh token sent in the body
 * (Capacitor native). Returns the new token on success, or null if the
 * user has no valid refresh token (logged out / expired).
 *
 * All concurrent callers share the same in-flight promise so a rotating
 * refresh token is never used twice.
 */
export async function refreshAccessToken(): Promise<string | null> {
  if (_refreshInFlight) return _refreshInFlight;
  // Capture in a local variable so the null reset inside `.finally()` does
  // not affect callers that are already awaiting this promise instance.
  const inflight = _doRefresh().finally(() => {
    _refreshInFlight = null;
  });
  _refreshInFlight = inflight;
  return inflight;
}

async function _doRefresh(): Promise<string | null> {
  const firebaseUser = await waitForFirebaseUser();
  if (firebaseUser) {
    const token = await firebaseUser.getIdToken(true);
    const res = await fetch(`${BASE}/api/auth/me`, {
      headers: { authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      await signOutFirebase().catch(() => undefined);
      return null;
    }
    const data = (await res.json()) as { user: SessionUser };
    setUser(data.user, token, null);
    return token;
  }

  const session = getSession();
  const body: Record<string, string> = {};
  const headers: Record<string, string> = { ...nativePlatformHeaders() };
  if (isNativePlatform() && session.refreshToken) {
    body.refreshToken = session.refreshToken;
    headers['content-type'] = 'application/json';
  }
  const res = await fetch(`${BASE}/api/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers,
    ...(Object.keys(body).length ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) return null;
  const data = (await res.json()) as RefreshResponse;
  setUser(data.user, data.accessToken, oneHourFromNow(), data.refreshToken);
  return data.accessToken;
}

async function rawFetch<T>(path: string, init?: RequestInit, retried = false): Promise<T> {
  const headers: Record<string, string> = {
    'content-type': 'application/json',
  };
  const accessToken = await currentAccessToken(retried);
  if (accessToken) {
    headers['authorization'] = `Bearer ${accessToken}`;
  } else {
    const dev = devHeader();
    if (dev) headers['x-tcg-dev-user'] = dev;
  }
  Object.assign(headers, (init?.headers ?? {}) as Record<string, string>);

  const res = await fetch(`${BASE}/api${path}`, {
    ...init,
    credentials: 'include',
    headers,
  });

  if (res.status === 401 && !retried) {
    const fresh = await refreshAccessToken();
    if (fresh) return rawFetch<T>(path, init, true);
    clearSession();
  }
  if (!res.ok) {
    const body = await res.text();
    const err = new Error(`API ${res.status}: ${body}`) as Error & { status?: number; body?: string };
    err.status = res.status;
    err.body = body;
    throw err;
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T,>(p: string, opts?: { signal?: AbortSignal }) => rawFetch<T>(p, { signal: opts?.signal }),
  getBlob: async (p: string): Promise<Blob> => {
    const headers: Record<string, string> = {};
    let accessToken = await currentAccessToken();
    if (accessToken) {
      headers['authorization'] = `Bearer ${accessToken}`;
    } else {
      const dev = devHeader();
      if (dev) headers['x-tcg-dev-user'] = dev;
    }

    const send = async (): Promise<Response> =>
      fetch(`${BASE}/api${p}`, {
        method: 'GET',
        credentials: 'include',
        headers,
      });

    let res = await send();
    if (res.status === 401) {
      const fresh = await refreshAccessToken();
      if (fresh) {
        accessToken = fresh;
        headers.authorization = `Bearer ${accessToken}`;
        res = await send();
      } else {
        clearSession();
      }
    }

    if (!res.ok) {
      const text = await res.text();
      throw new Error(`API ${res.status}: ${text}`);
    }
    return res.blob();
  },
  post: <T,>(p: string, body: unknown) =>
    rawFetch<T>(p, { method: 'POST', body: JSON.stringify(body) }),
  put: <T,>(p: string, body: unknown) =>
    rawFetch<T>(p, { method: 'PUT', body: JSON.stringify(body) }),
  patch: <T,>(p: string, body: unknown) =>
    rawFetch<T>(p, { method: 'PATCH', body: JSON.stringify(body) }),
  del: <T,>(p: string) => rawFetch<T>(p, { method: 'DELETE' }),
  /**
   * POST that returns the raw response body as a Blob (for PDF downloads
   * etc.). Reuses the same auth headers as `rawFetch` but doesn't try to
   * parse JSON.
   */
  postBlob: async (p: string, body: unknown): Promise<Blob> => {
    const headers: Record<string, string> = { 'content-type': 'application/json' };
    const accessToken = await currentAccessToken();
    if (accessToken) {
      headers['authorization'] = `Bearer ${accessToken}`;
    } else {
      const dev = devHeader();
      if (dev) headers['x-tcg-dev-user'] = dev;
    }
    const res = await fetch(`${BASE}/api${p}`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`API ${res.status}: ${text}`);
    }
    return res.blob();
  },
  /**
   * Multipart/form-data upload with the same auth + refresh behavior as JSON
   * requests. Used for large file imports where JSON payloads can exceed
   * gateway limits.
   */
  postForm: async <T,>(p: string, form: FormData, options?: PostFormOptions): Promise<T> => {
    const sendOnce = async (): Promise<{ status: number; bodyText: string }> => {
      const headers: Record<string, string> = {};
      const accessToken = await currentAccessToken();
      if (accessToken) {
        headers['authorization'] = `Bearer ${accessToken}`;
      } else {
        const dev = devHeader();
        if (dev) headers['x-tcg-dev-user'] = dev;
      }

      if (options?.onUploadProgress) {
        return sendFormWithXhr({
          path: p,
          form,
          headers,
          onUploadProgress: options.onUploadProgress,
        });
      }

      const res = await fetch(`${BASE}/api${p}`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: form,
      });
      return {
        status: res.status,
        bodyText: await res.text(),
      };
    };

    const send = async (): Promise<{ status: number; bodyText: string }> => {
      try {
        return await sendOnce();
      } catch (error) {
        if (error instanceof FormUploadNetworkError && error.uploadedBytes === 0) {
          // Safe retry: request did not begin sending payload bytes.
          return sendOnce();
        }
        throw error;
      }
    };

    let res = await send();
    if (res.status === 401) {
      const fresh = await refreshAccessToken();
      if (fresh) {
        res = await send();
      } else {
        clearSession();
      }
    }

    if (res.status < 200 || res.status >= 300) {
      throw new Error(`API ${res.status}: ${res.bodyText}`);
    }

    return parseJsonBody<T>(res.status, res.bodyText);
  },
};

/**
 * Login with email/password. Persists the resulting access token + user
 * into the global session.
 */
export async function login(email: string, password: string): Promise<SessionUser> {
  if (firebaseEnabled) {
    const user = await signInWithFirebaseEmail(email, password);
    return establishFirebaseSession(await user.getIdToken());
  }

  const res = await fetch(`${BASE}/api/auth/login`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...nativePlatformHeaders() },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`Login failed (${res.status}): ${body}`);
  }
  const data = (await res.json()) as RefreshResponse;
  setUser(data.user, data.accessToken, oneHourFromNow(), data.refreshToken);
  return data.user;
}

async function establishFirebaseSession(token: string): Promise<SessionUser> {
  const res = await fetch(`${BASE}/api/auth/me`, {
    headers: { authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    await signOutFirebase();
    const body = await res.text();
    throw new Error(`Account is not provisioned (${res.status}): ${body}`);
  }
  const data = (await res.json()) as { user: SessionUser };
  setUser(data.user, token, null);
  return data.user;
}

export async function loginWithGoogle(): Promise<SessionUser> {
  const user = await signInWithFirebaseGoogle();
  return establishFirebaseSession(await user.getIdToken());
}

export interface SignupInput {
  storeName: string;
  ownerName: string;
  ownerEmail: string;
  ownerPassword: string;
  timezone?: string;
  locationName?: string;
}

export interface SignupResult {
  accessToken: string;
  user: SessionUser;
  store: { id: string; name: string };
  location: { id: string; name: string };
  refreshToken?: string;
}

export async function signup(input: SignupInput): Promise<SignupResult> {
  if (firebaseEnabled) {
    const user = await createFirebaseOwner(input.ownerEmail, input.ownerPassword, input.ownerName);
    const token = await user.getIdToken();
    const res = await fetch(`${BASE}/api/auth/provision`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        storeName: input.storeName,
        ownerName: input.ownerName,
        timezone: input.timezone,
        locationName: input.locationName,
      }),
    });
    if (!res.ok) {
      await signOutFirebase().catch(() => undefined);
      const body = await res.text();
      throw new Error(`Signup failed (${res.status}): ${body}`);
    }
    const data = (await res.json()) as Omit<SignupResult, 'accessToken'>;
    setUser(data.user, token, null);
    return { ...data, accessToken: token };
  }

  const res = await fetch(`${BASE}/api/auth/signup`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...nativePlatformHeaders() },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`Signup failed (${res.status}): ${body}`);
  }
  const data = (await res.json()) as SignupResult & { expiresIn: number };
  setUser(data.user, data.accessToken, oneHourFromNow(), data.refreshToken);
  return data;
}

export async function logout(): Promise<void> {
  try {
    const session = getSession();
    const body: Record<string, string> = {};
    const headers: Record<string, string> = { ...nativePlatformHeaders() };
    if (isNativePlatform() && session.refreshToken) {
      body.refreshToken = session.refreshToken;
      headers['content-type'] = 'application/json';
    }
    await fetch(`${BASE}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers,
      ...(Object.keys(body).length ? { body: JSON.stringify(body) } : {}),
    });
  } finally {
    await signOutFirebase().catch(() => undefined);
    clearSession();
    setAccessToken(null);
  }
}

/**
 * Triggers a password-reset email. The server always returns 204 regardless
 * of whether the email matched a user (anti-enumeration), so a successful
 * resolution does NOT confirm the address exists.
 */
export async function requestPasswordReset(email: string): Promise<void> {
  if (firebaseEnabled) {
    await requestFirebasePasswordReset(email);
    return;
  }

  const res = await fetch(`${BASE}/api/auth/forgot-password`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok && res.status !== 204) {
    const body = await res.text();
    throw new Error(`Reset request failed (${res.status}): ${body}`);
  }
}

export async function confirmPasswordReset(token: string, password: string): Promise<void> {
  const res = await fetch(`${BASE}/api/auth/reset-password`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ token, password }),
  });
  if (!res.ok && res.status !== 204) {
    const body = await res.text();
    throw new Error(`Reset failed (${res.status}): ${body}`);
  }
}

/**
 * Check whether an email address is available for signup.
 * Best-effort UX hint only — not a security guarantee.
 */
export async function checkEmailAvailable(email: string): Promise<boolean> {
  const res = await fetch(
    `${BASE}/api/auth/check-email?email=${encodeURIComponent(email)}`,
    { credentials: 'include' },
  );
  if (!res.ok) return true; // fail open: don't block on network errors
  const data = (await res.json()) as { available: boolean };
  return data.available;
}

export interface OnboardingStatus {
  storeCreated: true;
  pricingConfigured: boolean;
  inventoryImported: boolean;
  posConfigured: boolean;
  completedAt: string | null;
}

export async function getOnboardingStatus(): Promise<OnboardingStatus> {
  return api.get<OnboardingStatus>('/settings/onboarding-status');
}

export async function completeOnboarding(): Promise<void> {
  await api.post('/settings/onboarding-complete', {});
}
